import { useCallback, useEffect, useRef, useState } from "react";
import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import type { HeldCredential } from "./held.ts";

export const ASK_AGAIN_EVERY = 2000;

export type SignInStep =
  | { step: "idle" }
  | { step: "opening" }
  | { step: "waiting"; signin: string; authorizeUrl: string; sending: boolean }
  | { step: "connected"; credential: HeldCredential }
  | { step: "refused"; why: string; startable: boolean };

export type SignInProgress =
  | { id: string; status: "waiting"; expiresAt: string }
  | { id: string; status: "connected"; credential: HeldCredential }
  | { id: string; status: "refused"; why: string }
  | { id: string; status: "expired"; why: string };

export function afterPolling(was: SignInStep, said: SignInProgress): SignInStep {
  if (said.status === "connected") return { step: "connected", credential: said.credential };
  if (said.status === "refused") return { step: "refused", why: said.why, startable: true };
  if (said.status === "expired") return { step: "refused", why: said.why, startable: true };
  return was;
}

export function theFallbackShows(step: SignInStep): boolean {
  return step.step === "waiting";
}

export function saidWhy(cause: unknown): string {
  return failureSaid(cause, say("head.broke"));
}

export function useSigningIn(provider: string, onConnected: () => void) {
  const [state, setState] = useState<SignInStep>({ step: "idle" });

  const asking = useRef<string | null>(null);

  const forget = useCallback(() => {
    asking.current = null;
    setState({ step: "idle" });
  }, []);

  const start = useCallback(async () => {
    setState({ step: "opening" });
    try {
      const said = await act<{ id: string; authorizeUrl: string }>("provider.signin.start", { provider });
      asking.current = said.id;

      globalThis.open?.(said.authorizeUrl, "_blank", "noopener,noreferrer");
      setState({ step: "waiting", signin: said.id, authorizeUrl: said.authorizeUrl, sending: false });
    } catch (cause) {
      setState({ step: "refused", why: saidWhy(cause), startable: true });
    }
  }, [provider]);

  const finish = useCallback(
    async (code: string) => {
      const signin = asking.current;
      if (!signin) return;
      setState((was) => (was.step === "waiting" ? { ...was, sending: true } : was));
      try {
        const said = await act<SignInProgress>("provider.signin.finish", { signin, code });
        setState((was) => afterPolling(was.step === "waiting" ? { ...was, sending: false } : was, said));
        if (said.status === "connected") onConnected();
      } catch (cause) {
        setState({ step: "refused", why: saidWhy(cause), startable: true });
      }
    },
    [onConnected],
  );

  useEffect(() => {
    if (state.step !== "waiting") return;
    const signin = state.signin;
    let stopped = false;
    const ask = async () => {
      try {
        const said = await act<SignInProgress>("provider.signin.poll", { signin });
        if (stopped || asking.current !== signin) return;
        setState((was) => afterPolling(was, said));
        if (said.status === "connected") onConnected();
      } catch (cause) {
        if (stopped) return;
        setState({ step: "refused", why: saidWhy(cause), startable: true });
      }
    };
    const timer = setInterval(() => void ask(), ASK_AGAIN_EVERY);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [state, onConnected]);

  return { state, start, finish, forget };
}
